import Icon from '../../Icon/Icon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import DataTable from 'react-data-table-component-footer';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../../containers/UseAuth';
import { useLocalStorage } from '../../../hooks/storage/useLocalStorage';
import { formatNumber, numberWithoutCommas } from '../../../utils/functions';
import { getVenueTypesList } from '../../../services/venueTypesService';
import {
    buildVenueTypeEntries,
    buildVenueTypeLabelLookup,
    extractVenueTypeLabel,
    normalizeVenueTypeValue,
} from '../../../utils/venueTypeLabels';
import MyResponsivePie from '../../Graph/Pie';
import styles from './ReportsTable.module.css';

const ReportsTable = (props) => {
    const {
        withGraph = false,
        data,
        currency,
        campaignName = '',
        rowTotals,
    } = props;
    const { t } = useTranslation();
    const csvLinkRef = useRef();
    const auth = useAuth();
    const userId = auth.session.user.id;
    const userName = auth.session.user.username;
    const { id } = useParams();
    const isDashboardIndividually = id ? true : false;
    const isAdminOrSupervisor = auth.isAdmin() || auth.isSupervising();
    const [currentPeriod] = useLocalStorage('currentPeriod', [null, null]);
    let from = currentPeriod[0];
    let to = currentPeriod[1];
    if (currentPeriod[0] && currentPeriod[1]) {
        from = currentPeriod[0].split(' ')[0];
        to = currentPeriod[1].split(' ')[0];
    }
    const [newCurrentData, setNewCurrentData] = useState([]);
    const [newCurrentColumns, setNewCurrentColumns] = useState([]);
    const [venueTypes, setVenueTypes] = useState([]);

    const nameCsv =
        t('sections.dashboard.reports-title') +
        `${isDashboardIndividually
            ? `_${id}_${campaignName}`
            : `_${userName}_from=${from}_to=${to}`
        }`;

    const initialRowTotals = {
        name: t('common.words.total'),
        id: 0,
        impressions: 0,
        ecpm: 0,
        spent: 0,
        impacts: 0,
        screenType: 0,
    };

    const [activeTab, setActiveTab] = useState(0);
    const [currentData, setCurrentData] = useState([]);
    const [graphData, setGraphData] = useState([]);
    const isScreensTab =
        data?.[activeTab]?.tabName === t('common.words.screens');

    const venueTypeLabelLookup = useMemo(
        () =>
            buildVenueTypeLabelLookup(
                buildVenueTypeEntries({
                    venueTypesCatalog: venueTypes,
                    includeCatalogEntries: true,
                })
            ),
        [venueTypes]
    );

    const changeTab = (value) => setActiveTab(value);

    const getVenueTypeLabel = (row) => {
        if (!row) return '-';

        const labels = [];

        const addLabelsFromValue = (value) => {
            if (!value) return;
            const candidates = Array.isArray(value) ? value : [value];

            candidates.forEach((candidate) => {
                let primaryValue = candidate;
                if (candidate && typeof candidate === 'object') {
                    primaryValue =
                        candidate.id ??
                        candidate.venue_type_id ??
                        candidate.value ??
                        candidate.code ??
                        candidate.name ??
                        candidate.label;
                }

                const normalized = normalizeVenueTypeValue(primaryValue);
                if (normalized && venueTypeLabelLookup.has(normalized)) {
                    labels.push(venueTypeLabelLookup.get(normalized));
                    return;
                }

                const fallbackLabel =
                    extractVenueTypeLabel(candidate) ||
                    (primaryValue !== null && primaryValue !== undefined
                        ? String(primaryValue).trim()
                        : '');

                if (fallbackLabel) labels.push(fallbackLabel);
            });
        };

        addLabelsFromValue(row?.venue_type);
        addLabelsFromValue(row?.venueType);
        addLabelsFromValue(row?.venue_type_id);
        addLabelsFromValue(row?.screenType);

        const uniqueLabels = Array.from(new Set(labels.filter(Boolean)));
        return uniqueLabels.length ? uniqueLabels.join(', ') : '-';
    };

    const currentTotals = () => {
        if (!rowTotals) {
            return {};
        }

        if (!rowTotals[activeTab]) {
            return initialRowTotals;
        }
        if (isDashboardIndividually) {
            return { ...rowTotals[activeTab], name: initialRowTotals.name };
        } else {
            return {
                ...rowTotals[activeTab][currency.short_name],
                name: initialRowTotals.name,
            };
        }
    };

    const formatColumn = (column, value, currency) => {
        let formattedValue = ''; // Degrade gracefully if no value is provided
        if (!isNaN(parseFloat(value))) {
            switch (column) {
                case 'id':
                    formattedValue = formatNumber(value, 'nomarker');
                    break;
                case 'impressions':
                case 'impacts':
                    formattedValue =
                        value == 0 ? '-' : formatNumber(value, 'rounded');
                    break;
                case 'ecpm':
                case 'spent':
                    formattedValue = formatNumber(
                        value,
                        'properCurrency',
                        null,
                        { currency: currency.short_name }
                    );
                    break;
            }
        } else {
            // FIXME: for some reason sometimes it's a string and other times it's an object
            if (typeof value === 'string') {
                formattedValue = formatNumber(value, 'rounded');
            } else if (typeof value === 'object') {
                return value.props?.children ? (
                    <b>{value.props?.children}</b>
                ) : (
                    value
                );
            }
        }
        return formattedValue;
    };

    const [totals, setTotals] = useState({});
    const customSort = (rowA, rowB, nameRow) => {
        const valueA = rowA?.[nameRow];
        const valueB = rowB?.[nameRow];

        if (valueA === valueB) {
            return 0;
        }

        const bothNumbers =
            typeof valueA === 'number' && typeof valueB === 'number';

        if (bothNumbers) {
            return valueA - valueB;
        }

        const normalizedA =
            valueA === undefined || valueA === null ? '' : `${valueA}`;
        const normalizedB =
            valueB === undefined || valueB === null ? '' : `${valueB}`;

        return normalizedA.localeCompare(normalizedB, undefined, {
            sensitivity: 'base',
            numeric: true,
        });
    };

    const currentColumns = useMemo(() => {
        if (currentData?.length === 0) return [];
        if (!data?.[activeTab]) return [];
        setTotals(currentTotals());

        const tabCampaign = t('common.words.campaign');
        const tabBrand = t('common.words.brand');
        const translations = 'common.components.ReportsTable.';
        const firstColumnKey =
            data[activeTab].columnName === tabBrand ? 'brand' : 'name';

        let firstColumn = {
            name: data[activeTab].columnName,
            key: firstColumnKey,
            label: data[activeTab].columnName,
            selector: firstColumnKey,
            sortable: true,
            sortFunction: (row1, row2) => customSort(row1, row2, firstColumnKey),
            left: true,
            minWidth: '30%',
        };

        if (data[activeTab].columnName !== tabBrand) {
            firstColumn.cell = (row) => (
                <div title={activeTab === 0 ? row[firstColumnKey] : ''}>
                    {row[firstColumnKey]}
                </div>
            );
        }

        if (data[activeTab].columnName === tabCampaign) {
            firstColumn.cell = (row) =>
                row.campaignid ? (
                    <Link to={row.link}>{row.name}</Link>
                ) : (
                    <div
                        title={
                            data[activeTab].columnName === tabBrand
                                ? row.brand
                                : row.name
                        }>
                        {data[activeTab].columnName === tabBrand
                            ? row.brand
                            : row.name}
                    </div>
                );
        }

        const styleLabel = {
            color: '#bdbdbd',
            fontSize: 10,
        };

        let defaultColumns =
            auth.isAdmin() || auth.isSupervising()
                ? [
                    {
                        name: t(translations + 'id'),
                        key: 'id',
                        label: t(translations + 'id'),
                        selector: (row) =>
                            formatColumn('id', row.id, currency),
                        left: true,
                        sortFunction: (row1, row2) =>
                            customSort(row1, row2, 'id'),
                    },
                    {
                        name: t(translations + 'impressions'),
                        key: 'impressions',
                        label: t(translations + 'impressions'),
                        selector: (row) =>
                            formatColumn(
                                'impressions',
                                row.impressions,
                                currency
                            ),
                        left: true,
                        sortFunction: (row1, row2) =>
                            customSort(row1, row2, 'impressions'),
                    },
                ]
                : [
                    {
                        name: t(translations + 'id'),
                        key: 'id',
                        label: t(translations + 'id'),
                        selector: (row) =>
                            formatColumn('id', row.id, currency),
                        left: true,
                        sortFunction: (row1, row2) =>
                            customSort(row1, row2, 'id'),
                    },
                ];

        let columnsToCSV = [...defaultColumns];
        let columnsToReports = [...defaultColumns];

        if (isScreensTab) {
            // A screen taken out of a subcampaign can lose its name in the
            // report, so its legacy screen id stays visible to keep it
            // recognisable. The UUID is left out on purpose: nobody reads it.
            const columnOfScreenIds = [
                {
                    name: t(translations + 'externalId'),
                    key: 'externalId',
                    label: t(translations + 'externalId'),
                    selector: (row) => row.externalId || '-',
                    cell: (row) => (
                        <div title={row.externalId || ''}>
                            {row.externalId || '-'}
                        </div>
                    ),
                    left: true,
                    minWidth: '140px',
                    sortFunction: (row1, row2) =>
                        customSort(row1, row2, 'externalId'),
                },
            ];

            columnsToReports = [...columnsToReports, ...columnOfScreenIds];
            columnsToCSV = [...columnsToCSV, ...columnOfScreenIds];
        }

        let columnOfImpacts = [
            {
                name: t(translations + 'impacts'),
                key: 'impacts',
                label: t(translations + 'impacts'),
                selector: (row) =>
                    formatColumn('impacts', row.impacts, currency),
                left: true,
                sortFunction: (row1, row2) => customSort(row1, row2, 'impacts'),
            },
        ];

        if (formatNumber(currentTotals().impacts) > 0) {
            columnsToReports = [...columnsToReports, ...columnOfImpacts];
        }
        columnsToCSV = [...columnsToCSV, ...columnOfImpacts];

        let columnOfEcpm = [
            {
                name: (
                    <span>
                        <b>{t(translations + 'ecpm')}</b>{' '}
                        <b style={styleLabel}>({currency.short_name})</b>
                    </span>
                ),
                key: 'ecpm',
                label: `${t(translations + 'ecpm')} (${currency.short_name})`,
                selector: (row) =>
                    formatColumn(
                        'ecpm',
                        row.impacts !== 0
                            ? (1000 * row.spent) / row.impacts
                            : row.ecpm,
                        currency
                    ),
                sortable: true,
                left: true,
                sortFunction: (row1, row2) => customSort(row1, row2, 'ecpm'),
            },
        ];

        columnsToReports = [...columnsToReports, ...columnOfEcpm];
        columnsToCSV = [...columnsToCSV, ...columnOfEcpm];

        let columnOfSpent = [
            {
                name: (
                    <span>
                        <b>{t(translations + 'spent')}</b>{' '}
                        <b style={styleLabel}>({currency.short_name})</b>
                    </span>
                ),
                key: 'spent',
                label: `${t(translations + 'spent')} (${currency.short_name})`,
                selector: (row) => formatColumn('spent', row.spent, currency),
                sortable: true,
                left: true,
                sortFunction: (row1, row2) => customSort(row1, row2, 'spent'),
            },
        ];

        columnsToReports = [...columnsToReports, ...columnOfSpent];
        columnsToCSV = [...columnsToCSV, ...columnOfSpent];

        if (isScreensTab) {
            let columnOfScreenType = [
                {
                    name: t(
                        'sections.management.section.pricing.tierLogs.columns.venueType'
                    ),
                    key: 'venueType',
                    label: t(
                        'sections.management.section.pricing.tierLogs.columns.venueType'
                    ),
                    selector: (row) => getVenueTypeLabel(row),
                    cell: (row) => {
                        const venueTypeLabel = getVenueTypeLabel(row);
                        return (
                            <div title={venueTypeLabel}>{venueTypeLabel}</div>
                        );
                    },
                    style: {
                        paddingLeft: '20px',
                    },
                    headerStyle: {
                        paddingLeft: '20px',
                    },
                    minWidth: '220px',
                    grow: 1.5,
                    sortFunction: (row1, row2) =>
                        getVenueTypeLabel(row1).localeCompare(
                            getVenueTypeLabel(row2),
                            undefined,
                            {
                                sensitivity: 'base',
                                numeric: true,
                            }
                        ),
                },
            ];

            columnsToReports = [...columnsToReports, ...columnOfScreenType];
            columnsToCSV = [...columnsToCSV, ...columnOfScreenType];
        }

        columnsToReports =
            firstColumn.name === tabCampaign
                ? columnsToReports
                : columnsToReports.filter((column) => column.key !== 'id');
        columnsToCSV =
            firstColumn.name === tabCampaign
                ? columnsToCSV
                : columnsToCSV.filter((column) => column.key !== 'id');

        return [firstColumn, ...columnsToReports];
    }, [currentData, currency, isScreensTab, t, venueTypeLabelLookup]);

    useEffect(() => {
        if (!isScreensTab || venueTypes.length > 0) return;

        let isMounted = true;

        const fetchVenueTypes = async () => {
            try {
                const venueTypesList = await getVenueTypesList();
                if (!isMounted) return;
                setVenueTypes(venueTypesList);
            } catch (error) {
                if (!isMounted) return;
                setVenueTypes([]);
                console.error('Error fetching venue types:', error);
            }
        };

        fetchVenueTypes();

        return () => {
            isMounted = false;
        };
    }, [isScreensTab, venueTypes.length]);

    useEffect(() => {
        setNewCurrentColumns(currentColumns);
    }, [currentColumns]);

    useEffect(() => {
        const arr = [];
        currentData.forEach((field) => {
            const object = {
                budget: field.budget,
                campaignid: field.campaignid,
                campaignname: field.campaignname,
                ecpm: field.ecpm,
                currency: field.currency,
                id: field.id,
                impacts: formatNumber(
                    formatNumber(field.impacts, 'rounded'),
                    'nomarker'
                ),
                impressions: field.impressions,
                link: field.link,
                name: field.name,
                externalId: field.externalId,
                spent: field.spent,
                screenType: field.screenType,
                venueType: getVenueTypeLabel(field),
            };

            arr.push(object);
        });

        setNewCurrentData(arr);
    }, [currentData, venueTypeLabelLookup]);

    useEffect(() => {
        if (data?.length > 0) {
            // ponytail: resetea activeTab si quedó fuera de rango tras reducirse data
            const safeTab = data[activeTab] ? activeTab : 0;
            if (safeTab !== activeTab) {
                setActiveTab(safeTab);
                return;
            }
            const currentData = data[activeTab].data;
            setCurrentData(currentData);
            let keyForGraphic = props.seeImpressions
                ? 'impressions'
                : 'impacts';
            const graphData = currentData.map((row) => ({
                id: row.name || row.brand,
                value:
                    row[keyForGraphic] === 0
                        ? 0
                        : numberWithoutCommas(row[keyForGraphic]),
            }));
            setGraphData(graphData);
        }
    }, [data, activeTab]);

    const noDataComponent = (
        <div className={styles.noContentContainer}>
            <span>{t('common.components.react-data-table.no-reports')}</span>
        </div>
    );

    const downloadToCSV = () => csvLinkRef.current.link.click();

    const actionsMemo = useMemo(() => {
        return currentData?.length > 0 ? (
            <div className="cursor-pointer mr-4" onClick={downloadToCSV}>
                <Icon
                    name="file-csv"
                    className="text-blue-300"
                />
                <span className="text-blue-300 text-sm pl-2">
                    {t('common.components.ReportsTable.export-to-csv')}
                </span>
            </div>
        ) : (
            <></>
        );
    }, [currentData]);

    return (
        <>
            <div className="flex justify-between">
                <div className="font-extralight text-xl p-2">
                    {t('sections.dashboard.reports')}
                </div>
            </div>
            <div className="module">
                <div className="flex">
                    <div className="w-full">
                        <nav className="flex">
                            {data.map((item, index) => (
                                <button
                                    key={index}
                                    onClick={() => changeTab(index)}
                                    className={`${activeTab === index
                                        ? 'active text-blue-500 border-b-2 font-medium border-blue-500'
                                        : (index == 2 && !isAdminOrSupervisor && (userId == 8046 || userId == 8042)) ? "hidden" : '' // HACK: hide the Screens tab for specific users
                                        } ${item.data?.length === 0 && 'hidden'
                                        } text-gray-600 py-2 font-light px-6 block hover:text-blue-500 focus:outline-none`}>
                                    {item.tabName}
                                </button>
                            ))}
                        </nav>
                        <div className="flex flex-col lg:flex-row">
                            <div className="w-full lg:w-9/12">
                                {/* HACK: Footer props should be better handled. It's different how we get data
                                automatically and reloading*/}
                                <DataTable
                                    columns={currentColumns}
                                    fixedHeader={true}
                                    fixedHeaderScrollHeight={'70vh'}
                                    subHeader={false}
                                    noDataComponent={noDataComponent}
                                    scrollCollapse
                                    data={currentData}
                                    actions={actionsMemo}
                                    footer={currentTotals() || totals}
                                />
                            </div>
                            {withGraph && (
                                <div
                                    className={`w-full lg:w-3/12 ${styles.graphContainer}`}>
                                    <MyResponsivePie
                                        enableArcLinkLabels={false}
                                        data={graphData}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <CSVLink
                    filename={`${nameCsv}.csv`}
                    headers={newCurrentColumns}
                    data={newCurrentData}
                    className={'hidden'}
                    ref={csvLinkRef}
                    target={'_blank'}
                />
            </div >
        </>
    );
};

export default ReportsTable;
